// pages/AwsPricing.jsx — AWS Pricing Calculator: pengganti AWS Pricing Calculator
// yang lebih mudah dipahami. Katalog harga adalah LAPISAN DATA; kalkulatornya
// adalah produknya.
//
// Empat tab: Kalkulator / Katalog Harga / Import / Sync & Config.
//
// Pembagian kerja dengan backend (Opsi B, sudah dikunci di Tahap 2):
//   backend  → menormalisasi harga menjadi matriks komitmen siap pakai
//   frontend → mengalikan dengan kuantitas, lalu Support & VAT
// Alasannya bukan pembagian beban: pola upfront/rate adalah tempat bug senyap
// bersembunyi, dan di backend pola itu bisa diverifikasi dengan query SQL.
// Yang tersisa di sini — aritmatika kuantitas — relatif jinak dan harus
// responsif tanpa memanggil API setiap kali angka jam diubah.
import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import useTabDeepLink from '../hooks/useTabDeepLink';
import { Calculator, Table2, RefreshCw, AlertTriangle, Upload } from 'lucide-react';
import AwsPricingCalculator from '../components/AwsPricingCalculator';
import AwsPricingCatalog from '../components/AwsPricingCatalog';
import AwsPricingImport from '../components/AwsPricingImport';
import AwsPricingSync from '../components/AwsPricingSync';

const TABS = [
  { key: 'calculator', labelKey: 'awsp.tab.calculator', icon: Calculator },
  { key: 'catalog',    labelKey: 'awsp.tab.catalog',    icon: Table2 },
  { key: 'import',     labelKey: 'awsp.tab.import',     icon: Upload },
  { key: 'sync',       labelKey: 'awsp.tab.sync',       icon: RefreshCw },
];

// Label tab jatuh ke teks Inggris bila key i18n belum ada, mengikuti pola tt()
// di AwsPricingCalculator. Tanpa ini 'awsp.tab.import' bocor mentah ke UI
// selama translations.js belum diperbarui.
const TAB_FALLBACK = { calculator: 'Calculator', catalog: 'Price Catalog', import: 'Import', sync: 'Sync & Config' };

export default function AwsPricing() {
  const { t } = useLang();
  const { user } = useAuth();
  const [active, setActive] = useState('calculator');
  const tabLabel = (tab) => {
    try { const v = t(tab.labelKey); return (v && v !== tab.labelKey) ? v : TAB_FALLBACK[tab.key]; }
    catch (_) { return TAB_FALLBACK[tab.key]; }
  };

  // ── Jembatan Import → Kalkulator ────────────────────────────────────────
  //
  // Keranjang hidup DI DALAM AwsPricingCalculator, dan tab di halaman ini
  // di-unmount saat tidak aktif (lihat render di bawah) — jadi tab Import
  // tidak bisa menyentuh keranjang secara langsung, dan keranjang lama memang
  // sudah hilang begitu pengguna berpindah tab.
  //
  // Konsekuensinya semantik impor adalah REPLACE, bukan append. Itu bukan
  // penyederhanaan: menggabungkan ke keranjang yang sudah tidak ada akan
  // menghasilkan janji yang tidak ditepati. Perilakunya sama persis dengan
  // memuat estimate tersimpan, yang juga mengganti seluruh keranjang.
  const [pendingImport, setPendingImport] = useState(null);
  const [calcRegion, setCalcRegion] = useState('ap-southeast-3');
  useTabDeepLink('aws_pricing', (p) => TABS.some(x => x.key === p), setActive);

  // Config (kurs, jam/bulan, VAT) dan tier Support dipakai oleh tab Kalkulator
  // maupun tab Sync & Config, jadi dimuat sekali di sini dan diturunkan.
  const [config, setConfig] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [cfgError, setCfgError] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/aws/pricing/config');
      const map = {};
      (res.data.config || []).forEach(r => { map[r.key] = r.value; });
      setConfig({ raw: res.data.config || [], map });
      setTiers(res.data.supportTiers || []);
      setCfgError(null);
    } catch (e) {
      setCfgError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div style={{ background: "linear-gradient(135deg, rgb(var(--brand-600)) 0%, rgb(var(--brand-400)) 100%)", borderRadius: 14, padding: '16px 22px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: 0, letterSpacing: '-0.4px' }}>{t('page.awsPricing.title')}</h1>
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', margin: '3px 0 0' }}>{t('page.awsPricing.subtitle')}</p>
      </div>

      <div className="border-b border-gray-200 flex gap-1 overflow-x-auto">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const on = active === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActive(tab.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap flex items-center gap-2 ${
                on ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              <Icon className="w-4 h-4" />
              {tabLabel(tab)}
            </button>
          );
        })}
      </div>

      {cfgError && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{t('awsp.configError')} {cfgError}</span>
        </div>
      )}

      {active === 'calculator' && (
        <AwsPricingCalculator
          config={config} tiers={tiers} loading={loading}
          pendingImport={pendingImport}
          onPendingConsumed={() => setPendingImport(null)}
          onRegionChange={setCalcRegion}
        />
      )}
      {active === 'catalog' && <AwsPricingCatalog />}
      {active === 'import' && (
        <AwsPricingImport
          region={calcRegion}
          onSendToCart={(payload) => {
            setPendingImport(payload);
            setCalcRegion(payload.region);
            setActive('calculator');
          }}
        />
      )}
      {active === 'sync' && (
        <AwsPricingSync
          config={config}
          tiers={tiers}
          isAdmin={user?.role === 'admin'}
          onConfigSaved={loadConfig}
        />
      )}
    </div>
  );
}
